import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, ChevronLeft, ChevronRight, Gamepad2, Mic, MicOff, Send, MessageSquare, ArrowUpRight, UserPlus, Bot, Clock, X, RotateCcw, Keyboard, Pencil, Save, ChevronDown, User, HelpCircle, FileText, Paperclip, Plus, Trash2, Hash, Mail } from 'lucide-react';
import { EmptyState } from './EmptyState';

/* ── Types ── */
type TaskState = 'pending_review' | 'approved' | 'delegated_waiting' | 'completed';
type CardFocus = 'source' | 'response' | 'actions';
type ViewMode = 'review' | 'refine';
type SubAction = null | 'amend' | 'delegate';
type DelegateStep = 'pick' | 'compose';
type DelegateTarget = 'self' | 'agent' | 'colleague';
interface AttachedFile {
  id: string;
  label: string;
  url: string;
  type: string;
}
interface ActionItem {
  id: string;
  description: string;
  status: 'resolved' | 'needs_input' | 'delegated';
  inputPrompt?: string;
  userInput?: string;
  agentProvided?: string;
  agentSource?: AttachedFile;
  isUserAdded?: boolean;
}
interface Task {
  id: string;
  state: TaskState;
  priority: 'urgent' | 'high' | 'normal' | 'low';
  source: {
    type: 'email' | 'slack' | 'calendar';
    sender: {
      name: string;
      handle: string;
    };
    receivedAt: string;
    rawContent: string;
  };
  summary: string;
  actionItems: ActionItem[];
  draft: {
    body: string;
    channel: 'email' | 'slack';
  };
}

/* ── Logic ── */
function canApprove(items: ActionItem[]): boolean {
  return items.every(ai => ai.status === 'resolved' || ai.status === 'delegated' || !!ai.agentProvided);
}
function allDelegated(items: ActionItem[]): boolean {
  return items.length > 0 && items.every(ai => ai.status === 'delegated');
}
function allActioned(items: ActionItem[]): boolean {
  return items.every(ai => ai.status === 'resolved' || ai.status === 'delegated');
}
const SOURCE_ICONS: Record<string, string> = {
  'google-drive': '📁',
  'confluence': '📘',
  'sharepoint': '📂',
  'local': '💻'
};
const COLLEAGUES = [{
  name: 'Paul',
  handle: 'paul@partner.co'
}, {
  name: 'Marta',
  handle: '@marta'
}, {
  name: 'Tom',
  handle: '@tom'
}, {
  name: 'Sarah',
  handle: '@sarah'
}];

/* ── Mock data ── */
const MOCK_TASKS: Task[] = [{
  id: 'task_01',
  state: 'pending_review',
  priority: 'high',
  source: {
    type: 'email',
    sender: {
      name: 'Paul',
      handle: 'paul@partner.co'
    },
    receivedAt: '10:32 AM',
    rawContent: 'Hi Matt,\n\nCould you send over the privacy policy link? I need it for the client proposal going out tomorrow.\n\nAlso, do you have the updated DPA we discussed last week? And can you confirm the data retention period for the new contract?\n\nThanks,\nPaul'
  },
  summary: 'Paul needs: privacy policy link, updated DPA, and data retention confirmation for client proposal (due tomorrow)',
  actionItems: [{
    id: 'ai_01',
    description: 'Privacy policy link',
    status: 'needs_input',
    inputPrompt: 'Confirm the privacy policy URL',
    agentProvided: 'https://contextloops.com/privacy'
  }, {
    id: 'ai_02',
    description: 'Updated DPA document',
    status: 'needs_input',
    inputPrompt: 'Provide or locate the updated DPA'
  }, {
    id: 'ai_03',
    description: 'Data retention period',
    status: 'needs_input',
    inputPrompt: 'Confirm the data retention period',
    agentProvided: '24 months as per our standard terms'
  }],
  draft: {
    body: 'Hi Paul,\n\nHere\'s the privacy policy: {{ai_01}}\n\nRegarding the DPA — {{ai_02}}\n\nOn data retention — {{ai_03}}\n\nLet me know if you need anything else.\n\nBest,\nMatt',
    channel: 'email'
  }
}, {
  id: 'task_02',
  state: 'pending_review',
  priority: 'normal',
  source: {
    type: 'slack',
    sender: {
      name: 'Marta',
      handle: '@marta'
    },
    receivedAt: '11:15 AM',
    rawContent: 'Hey Matt — just checking if you\'re still free for the design review at 3pm today? I\'ve updated the Figma with the new nav patterns.'
  },
  summary: 'Marta confirming 3pm design review — Figma updated with new nav patterns',
  actionItems: [{
    id: 'ai_04',
    description: 'Confirm attendance at 3pm',
    status: 'needs_input',
    inputPrompt: 'Confirm you can attend',
    agentProvided: 'Yes, 3pm works. I\'ll review the Figma beforehand.'
  }],
  draft: {
    body: 'Yep, 3pm works! I\'ll take a look at the Figma beforehand. See you then.',
    channel: 'slack'
  }
}, {
  id: 'task_03',
  state: 'pending_review',
  priority: 'urgent',
  source: {
    type: 'email',
    sender: {
      name: 'NHS Digital',
      handle: 'integration@nhsd.nhs.uk'
    },
    receivedAt: '9:05 AM',
    rawContent: 'Dear Matt,\n\nAs part of the DTAC compliance review, we require the following by end of week:\n\n1. Updated clinical safety case report (DCB0129)\n2. Evidence of penetration testing within the last 12 months\n3. Confirmation of your Cyber Essentials certification status\n\nPlease submit via the Digital Health Portal.\n\nRegards,\nNHS Digital Compliance Team'
  },
  summary: 'NHS Digital needs: clinical safety report, pen test evidence, and Cyber Essentials status — due end of week',
  actionItems: [{
    id: 'ai_05',
    description: 'Clinical safety case report (DCB0129)',
    status: 'needs_input',
    inputPrompt: 'Verify this is the correct report',
    agentProvided: 'DCB0129 Clinical Safety Case Report v3.2 (attached)',
    agentSource: {
      id: 'f1',
      label: 'DCB0129_Clinical_Safety_Case_v3.2.pdf',
      url: 'https://drive.google.com/file/d/1a2b3c',
      type: 'google-drive'
    }
  }, {
    id: 'ai_06',
    description: 'Penetration testing evidence',
    status: 'needs_input',
    inputPrompt: 'Verify this is the latest pen test certificate',
    agentProvided: 'CyberGuard Pen Test Report Jan 2026 (attached)',
    agentSource: {
      id: 'f2',
      label: 'TMA_PenTest_CyberGuard_Jan2026.pdf',
      url: 'https://drive.google.com/file/d/4d5e6f',
      type: 'google-drive'
    }
  }, {
    id: 'ai_07',
    description: 'Cyber Essentials certification',
    status: 'needs_input',
    inputPrompt: 'Confirm certification status',
    agentProvided: 'Current Cyber Essentials certification (valid until March 2027)'
  }],
  draft: {
    body: 'Dear NHS Digital Compliance Team,\n\nThank you for your email. Please find the requested items below:\n\n1. Clinical safety case report — {{ai_05}}\n2. Penetration testing evidence — {{ai_06}}\n3. Cyber Essentials — {{ai_07}}\n\nPlease let me know if you require anything further.\n\nBest regards,\nMatt',
    channel: 'email'
  }
}];

/* ── Hooks ── */
function useIsMobile(bp = 768) {
  const [m, setM] = useState(window.innerWidth < bp);
  useEffect(() => {
    const f = () => setM(window.innerWidth < bp);
    window.addEventListener('resize', f);
    return () => window.removeEventListener('resize', f);
  }, [bp]);
  return m;
}
interface GPState {
  connected: boolean;
  id: string;
  a: boolean;
  b: boolean;
  x: boolean;
  y: boolean;
  l1: boolean;
  r1: boolean;
  l2: number;
  r2: number;
  leftX: number;
  leftY: number;
  rightX: number;
  rightY: number;
}
function useGamepad(): GPState {
  const [s, setS] = useState<GPState>({
    connected: false,
    id: '',
    a: false,
    b: false,
    x: false,
    y: false,
    l1: false,
    r1: false,
    l2: 0,
    r2: 0,
    leftX: 0,
    leftY: 0,
    rightX: 0,
    rightY: 0
  });
  useEffect(() => {
    let id: number;
    const poll = () => {
      const gp = navigator.getGamepads()[0];
      if (gp) {
        const d = (v: number) => Math.abs(v) < 0.3 ? 0 : v;
        setS({
          connected: true,
          id: gp.id,
          a: gp.buttons[0]?.pressed ?? false,
          b: gp.buttons[1]?.pressed ?? false,
          x: gp.buttons[2]?.pressed ?? false,
          y: gp.buttons[3]?.pressed ?? false,
          l1: gp.buttons[4]?.pressed ?? false,
          r1: gp.buttons[5]?.pressed ?? false,
          l2: gp.buttons[6]?.value ?? 0,
          r2: gp.buttons[7]?.value ?? 0,
          leftX: d(gp.axes[0] ?? 0),
          leftY: d(gp.axes[1] ?? 0),
          rightX: d(gp.axes[2] ?? 0),
          rightY: d(gp.axes[3] ?? 0)
        });
      } else setS(p => p.connected ? {
        ...p,
        connected: false,
        id: ''
      } : p);
      id = requestAnimationFrame(poll);
    };
    id = requestAnimationFrame(poll);
    return () => cancelAnimationFrame(id);
  }, []);
  return s;
}
function useButtonEdge(pressed: boolean, cb: () => void) {
  const w = useRef(false);
  useEffect(() => {
    if (pressed && !w.current) cb();
    w.current = pressed;
  }, [pressed, cb]);
}

/* ── Style ── */
const PRI_CLS: Record<string, string> = {
  urgent: 'bg-red-500/20 text-red-400',
  high: 'bg-amber-500/20 text-amber-400',
  normal: 'bg-[#4a9a9a]/20 text-[#4a9a9a]',
  low: 'bg-[#8a8680]/20 text-[#8a8680]'
};
const SRC_E: Record<string, string> = {
  email: '✉',
  slack: '⌘',
  calendar: '📅'
};
const DELEGATED_HELP = 'When a task is fully delegated, clicking "Delegated" closes it and moves it to a waiting state. The agent tracks all delegated subtasks. When every subtask completes, the task resurfaces with resolved content, ready for final approval.';
const bTeal = 'border border-[#4a9a9a]/30 bg-[#4a9a9a]/15 text-[#4a9a9a] hover:bg-[#4a9a9a]/25';
const bTealSolid = 'border border-[#4a9a9a] bg-[#4a9a9a] text-[#141418] hover:bg-[#5bb5b5]';
const bGrey = 'border border-[#8a8680]/15 bg-[#8a8680]/10 text-[#8a8680] hover:bg-[#8a8680]/20';
const bAmber = 'border border-amber-500/30 bg-amber-500/15 text-amber-400 hover:bg-amber-500/25';
const bInput = 'border border-[#8a8680]/15 bg-[#141418]';
function HelpTooltip({
  text
}: {
  text: string;
}) {
  const [show, setShow] = useState(false);
  return <span className="relative inline-flex" data-id="element-2146"><button onClick={() => setShow(!show)} onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)} className="text-[#8a8680]/30 hover:text-[#8a8680]/60 transition-colors" data-id="element-2147"><HelpCircle size={13} data-id="element-2148" /></button><AnimatePresence data-id="element-2149">{show && <motion.div initial={{
        opacity: 0,
        y: 4
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 4
      }} className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 w-64 bg-[#1c1c22] border border-[#8a8680]/15 rounded-xl shadow-xl p-3 z-50 text-[11px] text-[#e8e4df]/70 leading-relaxed" data-id="element-2150">{text}</motion.div>}</AnimatePresence></span>;
}
function DraftBody({
  task,
  textScale,
  onPlaceholderClick
}: {
  task: Task;
  textScale: number;
  onPlaceholderClick?: (id: string) => void;
}) {
  return <div className="leading-relaxed whitespace-pre-wrap" style={{
    fontSize: `${14 * textScale}px`
  }} data-id="element-2151">{task.draft.body.split(/(\{\{.*?\}\})/g).map((part, i) => {
      const m = part.match(/\{\{(.+?)\}\}/);
      if (m) {
        const item = task.actionItems.find(a => a.id === m[1]);
        if (!item) return <span key={i} data-id="element-2152">{part}</span>;
        if (item.status === 'resolved' && item.userInput) return <span key={i} className="text-[#4a9a9a] bg-[#4a9a9a]/10 rounded px-1" data-id="element-2153">{item.userInput}</span>;
        if (item.status === 'delegated') return <span key={i} className="text-amber-400/60 bg-amber-500/10 rounded px-1 text-[12px]" data-id="element-2154">⏳ Delegated</span>;
        if (item.agentSource) return <button key={i} onClick={() => onPlaceholderClick?.(item.id)} className="inline-flex items-center gap-1 text-[#4a9a9a]/70 bg-[#4a9a9a]/10 hover:bg-[#4a9a9a]/20 rounded px-1.5 py-0.5 text-[12px] transition-colors cursor-pointer" data-id="element-2155"><FileText size={10} data-id="element-2156" /> {item.agentSource.label.length > 30 ? item.agentSource.label.slice(0, 30) + '…' : item.agentSource.label}</button>;
        if (item.agentProvided) return <button key={i} onClick={() => onPlaceholderClick?.(item.id)} className="inline-flex items-center gap-1 text-[#4a9a9a]/70 bg-[#4a9a9a]/10 hover:bg-[#4a9a9a]/20 rounded px-1.5 py-0.5 text-[12px] transition-colors cursor-pointer" data-id="element-2157">✓? {item.agentProvided.length > 40 ? item.agentProvided.slice(0, 40) + '…' : item.agentProvided}</button>;
        return <button key={i} onClick={() => onPlaceholderClick?.(item.id)} className="inline-flex items-center gap-1 text-amber-400/80 bg-amber-500/10 hover:bg-amber-500/20 rounded px-1.5 py-0.5 text-[12px] transition-colors cursor-pointer" data-id="element-2158">⬡ {item.description}</button>;
      }
      return <span key={i} className="text-[#e8e4df]/90" data-id="element-2159">{part}</span>;
    })}</div>;
}

/* ── DelegateCompose ── */
function DelegateCompose({
  item,
  delegateTo,
  onSend,
  onCancel
}: {
  item: ActionItem;
  delegateTo: {
    type: 'self' | 'colleague';
    name: string;
    handle: string;
  };
  onSend: (message: string, channel: 'slack' | 'email') => void;
  onCancel: () => void;
}) {
  const defaultMsg = delegateTo.type === 'self' ? `Reminder: ${item.description}` : `Hi ${delegateTo.name}, could you help with: ${item.description}?`;
  const [message, setMessage] = useState(defaultMsg);
  const [channel, setChannel] = useState<'slack' | 'email'>('slack');
  return <div className="flex flex-col gap-3" data-id="element-2160">
    <div className="flex items-center justify-between" data-id="element-2161"><p className="text-[10px] text-[#8a8680]/50 uppercase tracking-wider font-medium" data-id="element-2162">{delegateTo.type === 'self' ? 'Send to self' : `Message to ${delegateTo.name}`}</p><button onClick={onCancel} className="text-[#8a8680]/30 hover:text-[#8a8680]" data-id="element-2163"><X size={14} data-id="element-2164" /></button></div>
    <div className="flex gap-1.5" data-id="element-2165">
      <button onClick={() => setChannel('slack')} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium transition-all ${channel === 'slack' ? 'bg-[#4a9a9a]/15 text-[#4a9a9a] border border-[#4a9a9a]/30' : 'text-[#8a8680]/40 border border-transparent hover:text-[#8a8680]'}`} data-id="element-2166"><Hash size={11} data-id="element-2167" /> Slack</button>
      <button onClick={() => setChannel('email')} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium transition-all ${channel === 'email' ? 'bg-[#4a9a9a]/15 text-[#4a9a9a] border border-[#4a9a9a]/30' : 'text-[#8a8680]/40 border border-transparent hover:text-[#8a8680]'}`} data-id="element-2168"><Mail size={11} data-id="element-2169" /> Email</button>
    </div>
    <div className="flex items-center gap-2 text-[11px] text-[#8a8680]/50" data-id="element-2170"><span data-id="element-2171">To:</span><span className="text-[#e8e4df]/70" data-id="element-2172">{delegateTo.handle}</span></div>
    <textarea value={message} onChange={e => setMessage(e.target.value)} className={`w-full rounded-xl p-2.5 text-[#e8e4df] text-[12px] resize-none outline-none min-h-[60px] placeholder:text-[#8a8680]/30 ${bInput}`} data-id="element-2173" />
    <button onClick={() => onSend(message, channel)} disabled={!message} className={`flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-[12px] font-medium disabled:opacity-30 transition-all ${bTealSolid}`} data-id="element-2174"><Send size={11} data-id="element-2175" /> Send via {channel === 'slack' ? 'Slack' : 'Email'}</button>
  </div>;
}

/* ── SubtaskPanel ── */
function SubtaskPanel({
  item,
  subAction,
  setSubAction,
  listening,
  setListening,
  onAccept,
  onAmend,
  onDelegate,
  onDelete
}: {
  item: ActionItem;
  subAction: SubAction;
  setSubAction: (a: SubAction) => void;
  listening: boolean;
  setListening: (l: boolean) => void;
  onAccept: (text: string, files: AttachedFile[]) => void;
  onAmend: (instruction: string) => void;
  onDelegate: (type: string, name: string, handle: string, message: string, channel: string) => void;
  onDelete: () => void;
}) {
  const [editText, setEditText] = useState(item.agentProvided || '');
  const [files, setFiles] = useState<AttachedFile[]>(item.agentSource ? [item.agentSource] : []);
  const [amendInstruction, setAmendInstruction] = useState('');
  const [delegateStep, setDelegateStep] = useState<DelegateStep>('pick');
  const [delegateTo, setDelegateTo] = useState<{
    type: 'self' | 'colleague';
    name: string;
    handle: string;
  } | null>(null);
  useEffect(() => {
    setEditText(item.agentProvided || '');
    setFiles(item.agentSource ? [item.agentSource] : []);
  }, [item.id]);
  const removeFile = (fid: string) => setFiles(prev => prev.filter(f => f.id !== fid));
  const simulateUpload = () => setFiles(prev => [...prev, {
    id: 'f_' + Date.now(),
    label: 'uploaded_document.pdf',
    url: '#',
    type: 'local'
  }]);
  const simulateVoice = () => {
    setListening(true);
    setTimeout(() => {
      setEditText(prev => prev ? prev + ' (updated via voice)' : 'Response via voice');
      setListening(false);
    }, 1200);
  };
  if (item.status === 'resolved' && item.userInput) return <div className="bg-[#4a9a9a]/10 border border-[#4a9a9a]/20 rounded-xl p-3" data-id="element-2176"><p className="text-[10px] text-[#4a9a9a] uppercase tracking-wider font-medium flex items-center gap-1 mb-1" data-id="element-2177"><Check size={10} data-id="element-2178" /> Resolved</p><p className="text-[#e8e4df] text-[13px]" data-id="element-2179">{item.userInput}</p></div>;
  if (item.status === 'delegated') return <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-3" data-id="element-2180"><p className="text-[10px] text-amber-400 uppercase tracking-wider font-medium flex items-center gap-1 mb-1" data-id="element-2181"><Clock size={10} data-id="element-2182" /> Delegated</p><p className="text-[#e8e4df]/70 text-[13px]" data-id="element-2183">{item.userInput}</p></div>;
  if (!subAction) {
    return <div className="flex flex-col gap-3" data-id="element-2184">
      {item.inputPrompt && <p className="text-[#8a8680]/50 text-[12px]" data-id="element-2185">{item.inputPrompt}</p>}
      <div className={`rounded-xl overflow-hidden ${bInput}`} data-id="element-2186"><textarea value={editText} onChange={e => setEditText(e.target.value)} placeholder="Type or paste your response…" className="w-full bg-transparent p-2.5 pb-1 text-[#e8e4df] text-[12px] resize-none outline-none min-h-[48px] placeholder:text-[#8a8680]/30" data-id="element-2187" /><div className="flex items-center justify-end gap-1 px-2 pb-1.5" data-id="element-2188"><button onClick={simulateUpload} className="p-1 text-[#8a8680]/30 hover:text-[#8a8680] transition-colors" data-id="element-2189"><Paperclip size={12} data-id="element-2190" /></button><button onClick={simulateVoice} className={`p-1 transition-colors ${listening ? 'text-red-400 animate-pulse' : 'text-[#8a8680]/30 hover:text-[#4a9a9a]'}`} data-id="element-2191">{listening ? <MicOff size={12} data-id="element-2192" /> : <Mic size={12} data-id="element-2193" />}</button></div></div>
      {files.length > 0 && <div className="flex flex-col gap-1.5" data-id="element-2194">{files.map(f => <div key={f.id} className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-[#141418]/60 border border-[#8a8680]/10" data-id="element-2195"><span className="text-[12px]" data-id="element-2196">{SOURCE_ICONS[f.type] || '📄'}</span><a href={f.url} target="_blank" rel="noopener noreferrer" className="flex-1 min-w-0 text-[11px] text-[#e8e4df]/60 hover:text-[#4a9a9a] truncate" data-id="element-2197">{f.label}</a><button onClick={() => removeFile(f.id)} className="p-0.5 text-[#8a8680]/20 hover:text-red-400" data-id="element-2198"><X size={11} data-id="element-2199" /></button></div>)}</div>}
      <div className="flex gap-2" data-id="element-2200">
        <button onClick={() => onAccept(editText, files)} disabled={!editText && files.length === 0} className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-[12px] font-medium transition-all disabled:opacity-30 ${bTeal}`} data-id="element-2201"><Check size={13} data-id="element-2202" /> Accept</button>
        <button onClick={() => {
          setSubAction('amend');
          setAmendInstruction('');
        }} className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-[12px] font-medium transition-all ${bGrey}`} data-id="element-2203"><Bot size={13} data-id="element-2204" /> Amend</button>
        <button onClick={() => {
          setSubAction('delegate');
          setDelegateStep('pick');
          setDelegateTo(null);
        }} className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-[12px] font-medium transition-all ${bGrey}`} data-id="element-2205"><UserPlus size={13} data-id="element-2206" /> Delegate</button>
      </div>
    </div>;
  }
  if (subAction === 'amend') {
    return <div className="flex flex-col gap-3" data-id="element-2207">
      <div className="bg-[#8a8680]/6 border border-[#8a8680]/10 rounded-xl p-2.5" data-id="element-2208"><p className="text-[9px] text-[#8a8680]/40 uppercase tracking-wider font-medium mb-1" data-id="element-2209">Current</p><p className="text-[#e8e4df]/50 text-[11px]" data-id="element-2210">{editText || '(empty)'}</p></div>
      <div data-id="element-2211"><p className="text-[10px] text-[#4a9a9a]/60 uppercase tracking-wider font-medium flex items-center gap-1 mb-1.5" data-id="element-2212"><Bot size={10} data-id="element-2213" /> Instruction to agent</p><div className={`rounded-xl overflow-hidden ${bInput}`} data-id="element-2214"><textarea autoFocus value={amendInstruction} onChange={e => setAmendInstruction(e.target.value)} placeholder='e.g. "Use the v3.3 version"' className="w-full bg-transparent p-2.5 pb-1 text-[#e8e4df] text-[12px] resize-none outline-none min-h-[48px] placeholder:text-[#8a8680]/30" data-id="element-2215" /><div className="flex items-center justify-end px-2 pb-1.5" data-id="element-2216"><button onClick={() => {
              setListening(!listening);
              if (!listening) setTimeout(() => {
                setAmendInstruction('Use the v3.3 version from last week');
                setListening(false);
              }, 1200);
            }} className={`p-1 transition-colors ${listening ? 'text-red-400 animate-pulse' : 'text-[#8a8680]/30 hover:text-[#4a9a9a]'}`} data-id="element-2217">{listening ? <MicOff size={12} data-id="element-2218" /> : <Mic size={12} data-id="element-2219" />}</button></div></div></div>
      <div className="flex gap-2" data-id="element-2220"><button onClick={() => {
          if (amendInstruction) onAmend(amendInstruction);
        }} disabled={!amendInstruction} className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-[12px] font-medium disabled:opacity-30 transition-all ${bTealSolid}`} data-id="element-2221"><Send size={11} data-id="element-2222" /> Send to agent</button><button onClick={() => setSubAction(null)} className="text-[#8a8680]/40 text-[11px] hover:text-[#8a8680] px-3" data-id="element-2223">← Back</button></div>
    </div>;
  }
  if (subAction === 'delegate') {
    if (delegateStep === 'pick' && !delegateTo) {
      return <div className="flex flex-col gap-3" data-id="element-2224">
        <p className="text-[10px] text-[#8a8680]/50 uppercase tracking-wider font-medium" data-id="element-2225">Delegate to…</p>
        <button onClick={() => {
          setDelegateTo({
            type: 'self',
            name: 'Self',
            handle: 'me'
          });
          setDelegateStep('compose');
        }} className={`flex items-center gap-2 py-2.5 px-3 rounded-xl text-[12px] font-medium transition-all ${bGrey}`} data-id="element-2226"><Clock size={13} data-id="element-2227" /> Myself (reminder)</button>
        <button onClick={() => onDelegate('agent', '', '', item.description, '')} className={`flex items-center gap-2 py-2.5 px-3 rounded-xl text-[12px] font-medium transition-all ${bTeal}`} data-id="element-2228"><Bot size={13} data-id="element-2229" /> Agent (search/research)</button>
        <div className="h-px bg-[#8a8680]/8 my-1" data-id="element-2230" />
        <p className="text-[9px] text-[#8a8680]/30 uppercase tracking-wider" data-id="element-2231">Colleagues</p>
        {COLLEAGUES.map(c => <button key={c.name} onClick={() => {
          setDelegateTo({
            type: 'colleague',
            ...c
          });
          setDelegateStep('compose');
        }} className={`flex items-center gap-2 py-2 px-3 rounded-xl text-[12px] transition-all ${bGrey}`} data-id="element-2232"><User size={12} data-id="element-2233" /> {c.name} <span className="text-[9px] text-[#8a8680]/30 ml-auto" data-id="element-2234">{c.handle}</span></button>)}
        <button onClick={() => setSubAction(null)} className="text-[#8a8680]/40 text-[11px] hover:text-[#8a8680] self-start mt-1" data-id="element-2235">← Back</button>
      </div>;
    }
    if (delegateStep === 'compose' && delegateTo) {
      return <DelegateCompose item={item} delegateTo={delegateTo} onSend={(message, channel) => onDelegate(delegateTo.type === 'self' ? 'self' : `colleague:${delegateTo.name}`, delegateTo.name, delegateTo.handle, message, channel)} onCancel={() => {
        setDelegateStep('pick');
        setDelegateTo(null);
      }} data-id="element-2236" />;
    }
  }
  return null;
}

/* ────────────────────────────────────────────────────
   Add Subtask — single view
   Title + Summary + Delegate to (Me/Agent/Colleague)
   with inline message composer for Me/Colleague
   New subtasks are always created as delegated.
   ──────────────────────────────────────────────────── */

function AddSubtaskForm({
  onAdd,
  onCancel
}: {
  onAdd: (desc: string, delegateType: string, delegateName: string, delegateHandle: string, message: string, channel: string) => void;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [target, setTarget] = useState<DelegateTarget>('self');
  const [colleague, setColleague] = useState(COLLEAGUES[0]);
  const [colleagueOpen, setColleagueOpen] = useState(false);
  const [channel, setChannel] = useState<'slack' | 'email'>('slack');
  const [message, setMessage] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const fn = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setColleagueOpen(false);
    };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, []);

  // Auto-draft message when title/target changes
  useEffect(() => {
    if (!title) {
      setMessage('');
      return;
    }
    if (target === 'self') setMessage(`Reminder: ${title}`);else if (target === 'colleague') setMessage(`Hi ${colleague.name}, could you help with: ${title}?`);else setMessage(title);
  }, [title, target, colleague.name]);
  const canSubmit = title.length > 0;
  const handleSubmit = () => {
    if (!canSubmit) return;
    if (target === 'agent') {
      onAdd(title, 'agent', '', '', summary || title, '');
    } else if (target === 'self') {
      onAdd(title, 'self', 'Self', 'me', message, channel);
    } else {
      onAdd(title, `colleague:${colleague.name}`, colleague.name, colleague.handle, message, channel);
    }
  };
  return <div className="flex flex-col gap-3 p-4" data-id="element-2237">
    <p className="text-[10px] text-[#8a8680]/50 uppercase tracking-wider font-medium" data-id="element-2238">New subtask</p>

    {/* Title */}
    <div data-id="element-2239">
      <label className="text-[10px] text-[#8a8680]/40 uppercase tracking-wider mb-1 block" data-id="element-2240">Title</label>
      <input autoFocus value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Upload signed contract" className={`w-full rounded-xl px-2.5 py-2 text-[#e8e4df] text-[12px] outline-none placeholder:text-[#8a8680]/30 ${bInput}`} data-id="element-2241" />
    </div>

    {/* Summary (optional) */}
    <div data-id="element-2242">
      <label className="text-[10px] text-[#8a8680]/40 uppercase tracking-wider mb-1 block" data-id="element-2243">Details <span className="text-[#8a8680]/20" data-id="element-2244">(optional)</span></label>
      <textarea value={summary} onChange={e => setSummary(e.target.value)} placeholder="Any extra context…" className={`w-full rounded-xl p-2.5 text-[#e8e4df] text-[12px] resize-none outline-none min-h-[36px] placeholder:text-[#8a8680]/30 ${bInput}`} data-id="element-2245" />
    </div>

    {/* Delegate to */}
    <div data-id="element-2246">
      <label className="text-[10px] text-[#8a8680]/40 uppercase tracking-wider mb-1.5 block" data-id="element-2247">Delegate to</label>
      <div className="flex gap-1.5" data-id="element-2248">
        <button onClick={() => setTarget('self')} className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-[11px] font-medium transition-all ${target === 'self' ? 'bg-[#4a9a9a]/15 text-[#4a9a9a] border border-[#4a9a9a]/30' : `${bGrey}`}`} data-id="element-2249"><Clock size={12} data-id="element-2250" /> Me</button>
        <button onClick={() => setTarget('agent')} className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-[11px] font-medium transition-all ${target === 'agent' ? 'bg-[#4a9a9a]/15 text-[#4a9a9a] border border-[#4a9a9a]/30' : `${bGrey}`}`} data-id="element-2251"><Bot size={12} data-id="element-2252" /> Agent</button>
        <div ref={ref} className="relative" data-id="element-2253">
          <button onClick={() => {
            setTarget('colleague');
            setColleagueOpen(!colleagueOpen);
          }} className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-[11px] font-medium transition-all ${target === 'colleague' ? 'bg-[#4a9a9a]/15 text-[#4a9a9a] border border-[#4a9a9a]/30' : `${bGrey}`}`} data-id="element-2254">
            <UserPlus size={12} data-id="element-2255" /> {target === 'colleague' ? colleague.name : 'Colleague'} <ChevronDown size={10} className={`transition-transform ${colleagueOpen ? 'rotate-180' : ''}`} data-id="element-2256" />
          </button>
          <AnimatePresence data-id="element-2257">{colleagueOpen && <motion.div initial={{
              opacity: 0,
              y: -4
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -4
            }} className="absolute bottom-full mb-2 right-0 w-44 bg-[#1c1c22] border border-[#8a8680]/15 rounded-xl shadow-xl overflow-hidden z-40" data-id="element-2258">
            {COLLEAGUES.map(c => <button key={c.name} onClick={() => {
                setColleague(c);
                setColleagueOpen(false);
                setTarget('colleague');
              }} className={`w-full flex items-center gap-2 px-3 py-2 text-[12px] text-left transition-colors ${colleague.name === c.name && target === 'colleague' ? 'bg-[#4a9a9a]/10 text-[#4a9a9a]' : 'text-[#e8e4df]/80 hover:bg-[#4a9a9a]/10'}`} data-id="element-2259"><User size={11} className="text-[#8a8680]/40" data-id="element-2260" /> {c.name} <span className="text-[9px] text-[#8a8680]/30 ml-auto" data-id="element-2261">{c.handle}</span></button>)}
          </motion.div>}</AnimatePresence>
        </div>
      </div>
    </div>

    {/* Message composer (for self / colleague) */}
    {target !== 'agent' && <div className="flex flex-col gap-2" data-id="element-2262">
      <div className="flex items-center justify-between" data-id="element-2263">
        <div className="flex gap-1.5" data-id="element-2264">
          <button onClick={() => setChannel('slack')} className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-medium transition-all ${channel === 'slack' ? 'bg-[#4a9a9a]/15 text-[#4a9a9a] border border-[#4a9a9a]/30' : 'text-[#8a8680]/40 border border-transparent'}`} data-id="element-2265"><Hash size={10} data-id="element-2266" /> Slack</button>
          <button onClick={() => setChannel('email')} className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-medium transition-all ${channel === 'email' ? 'bg-[#4a9a9a]/15 text-[#4a9a9a] border border-[#4a9a9a]/30' : 'text-[#8a8680]/40 border border-transparent'}`} data-id="element-2267"><Mail size={10} data-id="element-2268" /> Email</button>
        </div>
        <span className="text-[10px] text-[#8a8680]/30" data-id="element-2269">To: {target === 'self' ? 'me' : colleague.handle}</span>
      </div>
      <textarea value={message} onChange={e => setMessage(e.target.value)} placeholder="Message…" className={`w-full rounded-xl p-2.5 text-[#e8e4df] text-[12px] resize-none outline-none min-h-[50px] placeholder:text-[#8a8680]/30 ${bInput}`} data-id="element-2270" />
    </div>}

    {/* Agent instruction (shown instead of message for agent) */}
    {target === 'agent' && title && <div className="bg-[#4a9a9a]/8 border border-[#4a9a9a]/15 rounded-xl p-2.5" data-id="element-2271">
      <p className="text-[9px] text-[#4a9a9a]/50 uppercase tracking-wider font-medium flex items-center gap-1 mb-1" data-id="element-2272"><Bot size={9} data-id="element-2273" /> Agent will attempt</p>
      <p className="text-[#e8e4df]/60 text-[11px]" data-id="element-2274">{title}{summary ? ` — ${summary}` : ''}</p>
    </div>}

    {/* Submit */}
    <div className="flex gap-2 mt-1" data-id="element-2275">
      <button onClick={handleSubmit} disabled={!canSubmit} className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-[12px] font-medium disabled:opacity-30 transition-all ${bTealSolid}`} data-id="element-2276">
        <Plus size={11} data-id="element-2277" /> Create {target === 'agent' ? '& Run' : '& Send'}
      </button>
      <button onClick={onCancel} className="text-[#8a8680]/40 text-[11px] hover:text-[#8a8680] px-3" data-id="element-2278">Cancel</button>
    </div>
  </div>;
}

/* ═══ DESKTOP ═══ */
function DesktopLayout({
  task,
  tasks,
  currentIndex,
  mode,
  focus,
  textScale,
  activeTabId,
  subAction,
  listening,
  setFocus,
  setMode,
  setActiveTabId,
  setSubAction,
  setListening,
  handleApprove,
  handleAcceptItem,
  handleAmendItem,
  handleDelegateItem,
  handleDeleteItem,
  handleAddItem,
  handleDelegatedClose,
  goNext,
  goPrev,
  gamepad
}: any) {
  const approved = task.state === 'approved';
  const delegatedWaiting = task.state === 'delegated_waiting';
  const ca = useMemo(() => canApprove(task.actionItems), [task.actionItems]);
  const ad = useMemo(() => allDelegated(task.actionItems), [task.actionItems]);
  const aa = useMemo(() => allActioned(task.actionItems), [task.actionItems]);
  const [showAddForm, setShowAddForm] = useState(false);
  useEffect(() => {
    if (aa && mode === 'refine' && !showAddForm) setMode('review');
  }, [aa, mode, setMode, showAddForm]);
  const openRefineTab = (itemId: string) => {
    setMode('refine');
    setActiveTabId(itemId);
    setSubAction(null);
    setShowAddForm(false);
  };
  const colBg = (col: CardFocus) => col === focus ? 'bg-[#222228]' : 'bg-[#1a1a1f]';
  return <div className="min-h-[calc(100vh-56px)] w-full flex flex-col relative" data-id="element-2279">
    <div className="absolute top-4 right-4 flex items-center gap-2 text-xs z-20" data-id="element-2280"><Gamepad2 size={14} className={gamepad.connected ? 'text-[#4a9a9a]' : 'text-[#8a8680]/30'} data-id="element-2281" /><span className={gamepad.connected ? 'text-[#4a9a9a]' : 'text-[#8a8680]/30'} data-id="element-2282">{gamepad.connected ? gamepad.id.split('(')[0]?.trim().slice(0, 24) || 'Connected' : 'No controller'}</span></div>
    <div className="flex items-center justify-between px-6 pt-4 pb-2" data-id="element-2283"><div className="flex items-center gap-3" data-id="element-2284"><span className={`text-[10px] uppercase tracking-[0.1em] font-medium px-2 py-0.5 rounded-full ${PRI_CLS[task.priority]}`} data-id="element-2285">{task.priority}</span><span className="text-[11px] text-[#8a8680]/40" data-id="element-2286">{SRC_E[task.source.type]} {task.source.sender.name} · {task.source.receivedAt}</span></div><div className="flex items-center gap-2 text-[11px] text-[#8a8680]/40" data-id="element-2287"><span data-id="element-2288">{currentIndex + 1} / {tasks.length}</span><button onClick={goPrev} disabled={currentIndex === 0} className="disabled:opacity-20 hover:text-[#e8e4df]" data-id="element-2289"><ChevronLeft size={14} data-id="element-2290" /></button><button onClick={goNext} disabled={currentIndex === tasks.length - 1} className="disabled:opacity-20 hover:text-[#e8e4df]" data-id="element-2291"><ChevronRight size={14} data-id="element-2292" /></button></div></div>
    <div className="flex-1 flex gap-2 px-4 pb-4 overflow-hidden min-h-0" data-id="element-2293">
      <AnimatePresence mode="wait" data-id="element-2294"><motion.div key={task.id} initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -12
        }} transition={{
          duration: 0.3
        }} className="flex gap-2 flex-1 min-h-0" data-id="element-2295">
        <div onClick={() => setFocus('source')} className={`w-1/3 shrink-0 rounded-2xl p-5 overflow-y-auto cursor-pointer flex flex-col gap-3 transition-colors duration-200 ${colBg('source')}`} data-id="element-2296"><span className="text-[10px] uppercase tracking-[0.1em] text-[#8a8680]/50 font-medium" data-id="element-2297">Incoming · {task.draft.channel}</span><p className="text-[#e8e4df] leading-relaxed whitespace-pre-wrap" style={{
              fontSize: `${14 * textScale}px`
            }} data-id="element-2298">{focus === 'source' ? task.source.rawContent : task.summary}</p>{focus === 'source' && <p className="text-[#8a8680]/25 text-[10px] mt-auto pt-2" data-id="element-2299">{task.source.sender.handle}</p>}</div>
        <div onClick={() => setFocus('response')} className={`w-1/3 shrink-0 rounded-2xl p-5 overflow-y-auto cursor-pointer flex flex-col gap-3 transition-colors duration-200 ${colBg('response')}`} data-id="element-2300"><div className="flex items-center gap-2" data-id="element-2301"><span className="text-[10px] uppercase tracking-[0.1em] text-[#8a8680]/50 font-medium" data-id="element-2302">Draft response</span>{!ca && <span className="text-[10px] text-amber-400/50" data-id="element-2303">· items to review</span>}</div><DraftBody task={task} textScale={textScale} onPlaceholderClick={openRefineTab} data-id="element-2304" /></div>
        <div onClick={() => setFocus('actions')} className={`w-1/3 shrink-0 rounded-2xl overflow-hidden flex flex-col transition-colors duration-200 ${colBg('actions')}`} data-id="element-2305">
          <AnimatePresence mode="wait" data-id="element-2306">
            {(approved || delegatedWaiting) && <motion.div key="done" initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} className="flex-1 flex flex-col items-center justify-center gap-2 p-4" data-id="element-2307">{approved && <><Check size={20} className="text-[#4a9a9a]" data-id="element-2308" /><span className="text-[#4a9a9a] text-[13px] font-medium" data-id="element-2309">Sent</span></>}{delegatedWaiting && <><Clock size={20} className="text-amber-400" data-id="element-2310" /><span className="text-amber-400 text-[13px] font-medium text-center" data-id="element-2311">Waiting on delegated tasks</span></>}</motion.div>}
            {!approved && !delegatedWaiting && mode === 'review' && <motion.div key="btns" initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} className="flex-1 flex flex-col items-stretch justify-center gap-3 p-5" data-id="element-2312">
              {ad ? <div className="flex flex-col items-center gap-2" data-id="element-2313"><button onClick={handleDelegatedClose} className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl text-[13px] font-medium transition-all ${bAmber}`} data-id="element-2314"><Clock size={14} data-id="element-2315" /> Delegated</button><HelpTooltip text={DELEGATED_HELP} data-id="element-2316" /></div> : <button onClick={handleApprove} disabled={!ca} className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl text-[13px] font-medium transition-all ${ca ? bTealSolid : 'border border-[#8a8680]/10 bg-[#8a8680]/10 text-[#8a8680]/30 cursor-not-allowed'}`} data-id="element-2317"><Check size={14} data-id="element-2318" /> {aa ? 'Approve Actions' : 'Approve'}</button>}
              <button onClick={() => {
                  setMode('refine');
                  setActiveTabId(task.actionItems[0]?.id);
                  setSubAction(null);
                  setShowAddForm(false);
                }} className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl text-[13px] font-medium hover:text-[#e8e4df] transition-all ${bGrey}`} data-id="element-2319"><MessageSquare size={14} data-id="element-2320" /> Refine</button>
              {gamepad.connected && <div className="flex flex-col items-center gap-1 mt-2 text-[9px] text-[#8a8680]/20" data-id="element-2321"><span data-id="element-2322">A — {ad ? 'Delegated' : 'Approve'}</span><span data-id="element-2323">X — Refine</span><span data-id="element-2324">L2/R2 — Prev/Next</span></div>}
            </motion.div>}
            {!approved && !delegatedWaiting && mode === 'refine' && <motion.div key="tabs" initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} className="flex-1 flex flex-col overflow-hidden" data-id="element-2325">
              <div className="flex items-center border-b border-[#8a8680]/10 shrink-0" data-id="element-2326"><div className="flex-1 flex overflow-x-auto" data-id="element-2327">{task.actionItems.map((item: ActionItem, idx: number) => <button key={item.id} onClick={() => {
                      setActiveTabId(item.id);
                      setSubAction(null);
                      setShowAddForm(false);
                    }} className={`flex-shrink-0 px-3 py-2.5 text-[11px] font-medium transition-all border-b-2 ${activeTabId === item.id && !showAddForm ? 'border-b-[#4a9a9a] text-[#e8e4df]' : 'border-b-transparent text-[#8a8680]/40 hover:text-[#8a8680]'}`} data-id="element-2328"><span className="flex items-center gap-1" data-id="element-2329">{(item.status === 'resolved' || item.status === 'delegated') && <Check size={9} className="text-[#4a9a9a]" data-id="element-2330" />}Subtask {idx + 1}</span></button>)}</div><button onClick={() => {
                    setShowAddForm(true);
                    setSubAction(null);
                  }} className={`px-2.5 py-2 shrink-0 transition-colors ${showAddForm ? 'text-[#4a9a9a]' : 'text-[#8a8680]/30 hover:text-[#4a9a9a]'}`} title="Add subtask" data-id="element-2331"><Plus size={13} data-id="element-2332" /></button></div>
              <div className="flex-1 overflow-y-auto" data-id="element-2333">
                {showAddForm ? <AddSubtaskForm onAdd={(desc, dt, dn, dh, msg, ch) => {
                    handleAddItem(desc, dt, dn, dh, msg, ch);
                    setShowAddForm(false);
                  }} onCancel={() => setShowAddForm(false)} data-id="element-2334" /> : task.actionItems.map((item: ActionItem) => item.id !== activeTabId ? null : <div key={item.id} className="p-4" data-id="element-2335"><div className="flex items-center justify-between mb-3" data-id="element-2336"><h4 className="text-[#e8e4df] text-[13px] font-medium" data-id="element-2337">{item.description}</h4>{item.isUserAdded && <button onClick={() => handleDeleteItem(item.id)} className="text-[#8a8680]/20 hover:text-red-400 transition-colors" data-id="element-2338"><Trash2 size={12} data-id="element-2339" /></button>}</div><SubtaskPanel item={item} subAction={subAction} setSubAction={setSubAction} listening={listening} setListening={setListening} onAccept={(text: string, files: AttachedFile[]) => handleAcceptItem(item.id, text, files)} onAmend={(instruction: string) => handleAmendItem(item.id, instruction)} onDelegate={(type: string, name: string, handle: string, message: string, channel: string) => handleDelegateItem(item.id, type, name, handle, message, channel)} onDelete={() => handleDeleteItem(item.id)} data-id="element-2340" /></div>)}
              </div>
              <div className="p-3 border-t border-[#8a8680]/10" data-id="element-2341"><button onClick={() => {
                    setMode('review');
                    setSubAction(null);
                    setShowAddForm(false);
                  }} className="w-full flex items-center justify-center gap-1.5 py-2 rounded-xl text-[#8a8680]/50 text-[11px] hover:text-[#8a8680] transition-colors" data-id="element-2342"><RotateCcw size={11} data-id="element-2343" /> Back</button></div>
            </motion.div>}
          </AnimatePresence>
        </div>
      </motion.div></AnimatePresence>
    </div>
  </div>;
}

/* ═══ MOBILE ═══ */
function MobileLayout({
  task,
  tasks,
  currentIndex,
  mode,
  setMode,
  subAction,
  setSubAction,
  listening,
  setListening,
  handleApprove,
  handleAcceptItem,
  handleAmendItem,
  handleDelegateItem,
  handleDeleteItem,
  handleAddItem,
  handleDelegatedClose,
  goNext,
  goPrev,
  activeTabId,
  setActiveTabId
}: any) {
  const approved = task.state === 'approved';
  const delegatedWaiting = task.state === 'delegated_waiting';
  const ca = useMemo(() => canApprove(task.actionItems), [task.actionItems]);
  const ad = useMemo(() => allDelegated(task.actionItems), [task.actionItems]);
  const [showAddForm, setShowAddForm] = useState(false);
  const openRefine = (itemId?: string) => {
    setMode('refine');
    setActiveTabId(itemId || task.actionItems[0]?.id);
    setSubAction(null);
    setShowAddForm(false);
  };
  return <div className="min-h-[calc(100vh-56px)] w-full flex flex-col bg-[#141418] relative" data-id="element-2344">
    <div className="flex items-center justify-between px-5 pt-4 pb-2 shrink-0" data-id="element-2345"><div className="flex items-center gap-2" data-id="element-2346"><span className={`text-[10px] uppercase tracking-[0.1em] font-medium px-2 py-0.5 rounded-full ${PRI_CLS[task.priority]}`} data-id="element-2347">{task.priority}</span><span className="text-[11px] text-[#8a8680]/40" data-id="element-2348">{SRC_E[task.source.type]} {task.source.sender.name}</span></div><div className="flex items-center gap-2 text-[11px] text-[#8a8680]/40" data-id="element-2349"><button onClick={goPrev} disabled={currentIndex === 0} className="disabled:opacity-20" data-id="element-2350"><ChevronLeft size={16} data-id="element-2351" /></button><span data-id="element-2352">{currentIndex + 1}/{tasks.length}</span><button onClick={goNext} disabled={currentIndex === tasks.length - 1} className="disabled:opacity-20" data-id="element-2353"><ChevronRight size={16} data-id="element-2354" /></button></div></div>
    <div className="flex-1 overflow-y-auto px-5 pb-32" data-id="element-2355">
      <div className="py-5" data-id="element-2356"><span className="text-[10px] uppercase tracking-[0.12em] text-[#8a8680]/40 font-medium" data-id="element-2357">Incoming · {task.draft.channel}</span><p className="text-[#e8e4df] text-[15px] leading-[1.7] mt-3 whitespace-pre-wrap" data-id="element-2358">{task.source.rawContent}</p></div>
      <div className="h-px bg-[#8a8680]/10" data-id="element-2359" />
      <div className="py-5" data-id="element-2360"><div className="flex items-center gap-2 mb-3" data-id="element-2361"><span className="text-[10px] uppercase tracking-[0.12em] text-[#8a8680]/40 font-medium" data-id="element-2362">Suggested reply</span>{!ca && <span className="text-[10px] text-amber-400/50" data-id="element-2363">· items need review</span>}</div><DraftBody task={task} textScale={1} onPlaceholderClick={openRefine} data-id="element-2364" /></div>
    </div>
    {!approved && !delegatedWaiting && mode === 'review' && <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#141418] via-[#141418] to-transparent pt-8 pb-5 px-5" data-id="element-2365"><div className="flex gap-2" data-id="element-2366">
      {ad ? <div className="flex-1 flex items-center justify-center gap-2" data-id="element-2367"><button onClick={handleDelegatedClose} className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-2xl text-[14px] font-medium ${bAmber}`} data-id="element-2368"><Clock size={15} data-id="element-2369" /> Delegated</button><HelpTooltip text={DELEGATED_HELP} data-id="element-2370" /></div> : <button onClick={handleApprove} disabled={!ca} className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-2xl text-[14px] font-medium transition-all ${ca ? bTealSolid : 'border border-[#8a8680]/10 bg-[#8a8680]/10 text-[#8a8680]/30'}`} data-id="element-2371"><Check size={15} data-id="element-2372" /> Approve</button>}
      <button onClick={() => openRefine()} className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-2xl text-[14px] font-medium ${bGrey}`} data-id="element-2373"><MessageSquare size={15} data-id="element-2374" /> Refine</button>
    </div></div>}
    {approved && <div className="absolute bottom-0 left-0 right-0 bg-[#141418] pb-5 pt-3 flex items-center justify-center gap-2 text-[#4a9a9a]" data-id="element-2375"><Check size={16} data-id="element-2376" /> Sent</div>}
    {delegatedWaiting && <div className="absolute bottom-0 left-0 right-0 bg-[#141418] pb-5 pt-3 flex items-center justify-center gap-2 text-amber-400" data-id="element-2377"><Clock size={16} data-id="element-2378" /> Waiting</div>}
    <AnimatePresence data-id="element-2379">
      {mode === 'refine' && <motion.div initial={{
        y: '100%'
      }} animate={{
        y: 0
      }} exit={{
        y: '100%'
      }} transition={{
        type: 'spring',
        damping: 30,
        stiffness: 300
      }} className="absolute bottom-0 left-0 right-0 bg-[#1c1c22] rounded-t-3xl z-20 max-h-[85vh] flex flex-col" data-id="element-2380">
        <div className="flex items-center justify-between px-5 pt-4 pb-2 shrink-0" data-id="element-2381"><div className="flex gap-1 overflow-x-auto flex-1" data-id="element-2382">{task.actionItems.map((item: ActionItem, idx: number) => <button key={item.id} onClick={() => {
              setActiveTabId(item.id);
              setSubAction(null);
              setShowAddForm(false);
            }} className={`flex-shrink-0 px-2.5 py-1.5 text-[11px] font-medium rounded-lg transition-all ${activeTabId === item.id && !showAddForm ? 'bg-[#4a9a9a]/15 text-[#e8e4df]' : 'text-[#8a8680]/40'}`} data-id="element-2383">{(item.status === 'resolved' || item.status === 'delegated') && <Check size={9} className="text-[#4a9a9a] mr-1 inline" data-id="element-2384" />}{idx + 1}</button>)}<button onClick={() => {
              setShowAddForm(true);
              setSubAction(null);
            }} className={`flex-shrink-0 px-2 py-1.5 ${showAddForm ? 'text-[#4a9a9a]' : 'text-[#8a8680]/30 hover:text-[#4a9a9a]'}`} data-id="element-2385"><Plus size={13} data-id="element-2386" /></button></div><button onClick={() => {
            setMode('review');
            setShowAddForm(false);
          }} className="text-[#8a8680]/50 p-1 shrink-0 ml-2" data-id="element-2387"><X size={18} data-id="element-2388" /></button></div>
        <div className="h-px bg-[#8a8680]/10" data-id="element-2389" />
        <div className="flex-1 overflow-y-auto px-5 py-4" data-id="element-2390">
          {showAddForm ? <AddSubtaskForm onAdd={(desc, dt, dn, dh, msg, ch) => {
            handleAddItem(desc, dt, dn, dh, msg, ch);
            setShowAddForm(false);
          }} onCancel={() => setShowAddForm(false)} data-id="element-2391" /> : task.actionItems.filter((a: ActionItem) => a.id === activeTabId).map((item: ActionItem) => <div key={item.id} data-id="element-2392"><div className="flex items-center justify-between mb-3" data-id="element-2393"><h4 className="text-[#e8e4df] text-[14px] font-medium" data-id="element-2394">{item.description}</h4>{item.isUserAdded && <button onClick={() => handleDeleteItem(item.id)} className="text-[#8a8680]/20 hover:text-red-400" data-id="element-2395"><Trash2 size={13} data-id="element-2396" /></button>}</div><SubtaskPanel item={item} subAction={subAction} setSubAction={setSubAction} listening={listening} setListening={setListening} onAccept={(text: string, files: AttachedFile[]) => handleAcceptItem(item.id, text, files)} onAmend={(instruction: string) => handleAmendItem(item.id, instruction)} onDelegate={(type: string, name: string, handle: string, message: string, channel: string) => handleDelegateItem(item.id, type, name, handle, message, channel)} onDelete={() => handleDeleteItem(item.id)} data-id="element-2397" /></div>)}
        </div>
      </motion.div>}
    </AnimatePresence>
  </div>;
}

/* ═══ MAIN ═══ */
export function TaskReview() {
  const isMobile = useIsMobile();
  const gamepad = useGamepad();
  const [tasks, setTasks] = useState<Task[]>(MOCK_TASKS);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [focus, setFocus] = useState<CardFocus>('source');
  const [mode, setMode] = useState<ViewMode>('review');
  const [textScale, setTextScale] = useState(1.0);
  const [activeTabId, setActiveTabId] = useState(MOCK_TASKS[0]?.actionItems[0]?.id || '');
  const [subAction, setSubAction] = useState<SubAction>(null);
  const [listening, setListening] = useState(false);
  const currentTask = tasks[currentIndex];
  const resetNav = () => {
    setFocus('source');
    setMode('review');
    setSubAction(null);
    setListening(false);
  };
  const goNext = useCallback(() => {
    setCurrentIndex(prev => Math.min(prev + 1, tasks.length - 1));
    resetNav();
  }, [tasks.length]);
  const goPrev = useCallback(() => {
    setCurrentIndex(prev => Math.max(prev - 1, 0));
    resetNav();
  }, []);
  useEffect(() => {
    if (currentTask) setActiveTabId(currentTask.actionItems[0]?.id || '');
  }, [currentTask?.id]);
  const updateTask = useCallback((fn: (t: Task) => Task) => {
    setTasks(prev => prev.map((t, i) => i === currentIndex ? fn(t) : t));
  }, [currentIndex]);
  const handleAcceptItem = useCallback((itemId: string, text: string, files: AttachedFile[]) => {
    const display = files.length > 0 ? `${text}${text ? ' ' : ''}[${files.map(f => f.label).join(', ')}]` : text;
    updateTask(t => ({
      ...t,
      actionItems: t.actionItems.map(a => a.id === itemId ? {
        ...a,
        status: 'resolved' as const,
        userInput: display
      } : a)
    }));
  }, [updateTask]);
  const handleAmendItem = useCallback((itemId: string, instruction: string) => {
    updateTask(t => ({
      ...t,
      actionItems: t.actionItems.map(a => a.id === itemId ? {
        ...a,
        agentProvided: `${a.agentProvided || ''} [agent amended: ${instruction}]`
      } : a)
    }));
    setSubAction(null);
  }, [updateTask]);
  const handleDelegateItem = useCallback((itemId: string, type: string, name: string, handle: string, message: string, channel: string) => {
    const label = type === 'agent' ? '[agent] Searching…' : `[${type}${name ? ':' + name : ''}] via ${channel || 'slack'}: ${message.slice(0, 50)}`;
    updateTask(t => ({
      ...t,
      actionItems: t.actionItems.map(a => a.id === itemId ? {
        ...a,
        status: 'delegated' as const,
        userInput: label
      } : a)
    }));
    setSubAction(null);
  }, [updateTask]);
  const handleDeleteItem = useCallback((itemId: string) => {
    updateTask(t => ({
      ...t,
      actionItems: t.actionItems.filter(a => a.id !== itemId)
    }));
    setActiveTabId(prev => {
      const t = tasks[currentIndex];
      const remaining = t?.actionItems.filter(a => a.id !== prev);
      return remaining?.[0]?.id || '';
    });
  }, [updateTask, tasks, currentIndex]);

  // New subtasks are created already delegated
  const handleAddItem = useCallback((desc: string, delegateType: string, delegateName: string, delegateHandle: string, message: string, channel: string) => {
    const newId = 'user_' + Date.now();
    const label = delegateType === 'agent' ? '[agent] Searching…' : `[${delegateType}${delegateName ? ':' + delegateName : ''}] via ${channel || 'slack'}: ${message.slice(0, 50)}`;
    updateTask(t => ({
      ...t,
      actionItems: [...t.actionItems, {
        id: newId,
        description: desc,
        status: 'delegated' as const,
        userInput: label,
        isUserAdded: true
      }]
    }));
    setActiveTabId(newId);
  }, [updateTask]);
  const handleApprove = useCallback(() => {
    if (!currentTask || !canApprove(currentTask.actionItems)) return;
    updateTask(t => ({
      ...t,
      state: 'approved' as const,
      actionItems: t.actionItems.map(ai => ai.status === 'needs_input' && ai.agentProvided ? {
        ...ai,
        status: 'resolved' as const,
        userInput: ai.agentProvided
      } : ai)
    }));
    setTimeout(goNext, 500);
  }, [currentTask, updateTask, goNext]);
  const handleDelegatedClose = useCallback(() => {
    updateTask(t => ({
      ...t,
      state: 'delegated_waiting' as const
    }));
    setTimeout(goNext, 500);
  }, [updateTask, goNext]);
  const jd = useRef(0);
  useEffect(() => {
    if (!gamepad.connected || isMobile) return;
    const n = Date.now();
    if (n - jd.current < 200) return;
    if (gamepad.leftX > 0.5) {
      setFocus('response');
      jd.current = n;
    }
    ;
    if (gamepad.leftX < -0.5) {
      setFocus('source');
      jd.current = n;
    }
  }, [gamepad.leftX, gamepad.connected, isMobile]);
  useButtonEdge(gamepad.a, useCallback(() => {
    if (isMobile || !currentTask) return;
    const ad2 = allDelegated(currentTask.actionItems);
    if (mode === 'review') {
      if (ad2) handleDelegatedClose();else if (canApprove(currentTask.actionItems)) handleApprove();
    }
  }, [mode, currentTask, handleApprove, handleDelegatedClose, isMobile]));
  useButtonEdge(gamepad.x, useCallback(() => {
    if (isMobile) return;
    if (mode === 'review') {
      setMode('refine');
      setActiveTabId(currentTask?.actionItems[0]?.id || '');
    } else {
      setMode('review');
      setSubAction(null);
    }
  }, [mode, currentTask, isMobile]));
  useButtonEdge(gamepad.b, useCallback(() => {
    if (isMobile) return;
    if (subAction) setSubAction(null);else if (mode === 'refine') setMode('review');
  }, [mode, subAction, isMobile]));
  useButtonEdge(gamepad.l1, useCallback(() => setTextScale(p => Math.max(0.7, p - 0.1)), []));
  useButtonEdge(gamepad.r1, useCallback(() => setTextScale(p => Math.min(1.6, p + 0.1)), []));
  useButtonEdge(gamepad.l2 > 0.5, useCallback(() => goPrev(), [goPrev]));
  useButtonEdge(gamepad.r2 > 0.5, useCallback(() => goNext(), [goNext]));
  if (!currentTask) return <div className="min-h-[calc(100vh-56px)] w-full flex items-center justify-center px-6" data-id="element-2398"><EmptyState message="All caught up." data-id="element-2399" /></div>;
  const props = {
    task: currentTask,
    tasks,
    currentIndex,
    mode,
    focus,
    textScale,
    activeTabId,
    subAction,
    listening,
    setFocus,
    setMode,
    setActiveTabId,
    setSubAction,
    setListening,
    handleApprove,
    handleAcceptItem,
    handleAmendItem,
    handleDelegateItem,
    handleDeleteItem,
    handleAddItem,
    handleDelegatedClose,
    goNext,
    goPrev,
    gamepad
  };
  return isMobile ? <MobileLayout {...props} data-id="element-2400" /> : <DesktopLayout {...props} data-id="element-2401" />;
}